import { DragEvent, FC, useCallback, useContext, useEffect, useState } from 'react';
import { Spinner } from '@chakra-ui/react';
import * as Sentry from '@sentry/browser';
import { AUTH_STORAGE_KEY } from 'ui/types';
import { ICON_BY_NAME } from '../Icons/Icon';
import styles from './CbFileUploadInput.module.css';
import { getDirectUploadURL } from '../../../utils';
import { AppContext } from '../../../providers';
import { bytesToSize } from 'ui/helpers/bytes';
export interface CbFileUploadInputProps {
  id?: string;
  accept?: string;
  label?: string;
  maximumKilobytes?: number;
  multiple?: boolean;
  onUpload?: (attrs: ActiveStorageBlob) => void;
}
export interface ActiveStorageBlob {
  key: string;
  url: string;
  signed_blob_id: string;
}
export const CbFileUploadInput: FC<CbFileUploadInputProps> = ({
  id = 'attachment',
  accept = '*',
  label,
  maximumKilobytes = Number.POSITIVE_INFINITY,
  multiple = false,
  onUpload = () => {}
}) => {
  const [dropping, setDropping] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<string>();
  const pageLeave: OnBeforeUnloadEventHandler = useCallback(event => {
    if (!uploading) return null;
    const message = 'Files are still uploading. If you leave now, they will be lost.';
    event.returnValue = message;
    return message;
  }, [uploading]);
  const {
    setToast
  } = useContext(AppContext);
  const onPaste = event => {
    if (event.clipboardData.files.length) {
      transferFiles(event.clipboardData.files);
    }
  };
  useEffect(() => {
    window.addEventListener('onbeforeunload', pageLeave);
    window.addEventListener('paste', onPaste);
    return () => {
      window.removeEventListener('onbeforeunload', pageLeave);
      window.removeEventListener('paste', onPaste);
    };
  }, [pageLeave]);

  // Drag and drop handlers:
  //
  const dragEnter = (event: DragEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setDropping(true);
  };
  const dragOver = (event: DragEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
  };
  const dragLeave = (event: DragEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setDropping(false);
  };
  const drop = (event: DragEvent<HTMLInputElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setDropping(false);
    transferFiles(event.dataTransfer.files);
  };
  const manualUpload = (e: any) => {
    transferFiles(e.target.files);
  };
  const validateFiles = (files: FileList) => {
    const errors: string[] = [];
    if (!multiple && files.length > 1) {
      errors.push('Only one file upload is permitted');
    }
    for (const file of files) {
      if (accept !== '*' && accept.indexOf(file.type) === -1) {
        errors.push(`${file.name} is not in the expected format`);
      }
      if (file.size / 1000 > maximumKilobytes) {
        errors.push(`${file.name} is too large (Max file size is ${bytesToSize(maximumKilobytes * 1024)})`);
        Sentry.captureMessage('User has tried to upload a file that is too large', {
          extra: {
            size: file.size / 1000,
            name: file.name
          }
        });
      }
    }
    setErrors(errors.join(', '));
    return errors.length === 0;
  };
  const transferFiles = async (files: FileList) => {
    if (!validateFiles(files)) return;
    const {
      DirectUpload
    } = await import('@rails/activestorage');
    const auth = JSON.parse(window?.localStorage.getItem(AUTH_STORAGE_KEY) || '{}');
    setUploading(true);
    const promises: Promise<void>[] = [];
    for (const file of files) {
      promises.push(new Promise((resolve, reject) => {
        const upload = new DirectUpload(file, getDirectUploadURL(), {}, {
          'X-User-Token': auth.token
        });
        upload.create((err, success) => err ? reject(err) : resolve(success));
      }).then(onUpload));
    }
    if (promises.length) return Promise.all(promises).catch(() => setToast({
      description: 'Your files could not be uploaded.',
      status: 'error'
    })).finally(() => setUploading(false));
  };
  return <div className={`${styles.target} ${dropping ? styles.dropping : ''}`} data-sentry-component="CbFileUploadInput" data-sentry-source-file="CbFileUploadInput.tsx">
      <label htmlFor={id} className={styles.dragContainer}>
        <div style={{
        pointerEvents: 'none',
        visibility: uploading ? 'hidden' : 'visible'
      }}>
          <ICON_BY_NAME.file className={styles.icon} height="8" width="8" marginBottom="4" data-sentry-element="unknown" data-sentry-source-file="CbFileUploadInput.tsx" />
          <div className={styles.label}>
            {label || 'Click to Select a File'}
          </div>

          <div className={styles.dndText}>or Drag and Drop</div>

          {errors && <div className={styles.errors}>{errors}</div>}
        </div>

        {uploading && <div className={styles.spinner}>
            <Spinner color="chonky.500" thickness="4px" speed="0.65s" size="xl" />
          </div>}
      </label>
      <input type="file" id={id} name={id} onChange={manualUpload} accept={accept} multiple={multiple} onDragEnter={dragEnter} onDragOver={dragOver} onDragLeave={dragLeave} onDrop={drop} className={styles.hiddenInput} />
    </div>;
};